import { useState } from 'react';
import QuemMarcou from './QuemMarcou';
import { diaMesHora, ehCancelada, motivoDoCancelamento, quandoMarcou } from '../presencaFormacao';
import type { InscritoPresenca, ComoPresenca } from '../types';

type TipoLinha = 'presente' | 'recusada' | 'falta';

interface Props {
  linha: InscritoPresenca;
  tipo: TipoLinha;
  salvando: boolean;
  liberando: boolean;
  aoMarcar: (inscricaoId: number, obs: string) => void;
  aoLiberarReenvio: (presencaId: number) => void;
  formacaoQuando?: string | null;
  aoCancelar?: (linha: InscritoPresenca) => void;
  aoDesfazer?: (linha: InscritoPresenca) => void;
  desfazendo?: boolean;
}

function iconeComo(como: ComoPresenca): string {
  if (como === 'qr') return 'ri-qr-code-line';
  if (como === 'foto') return 'ri-camera-line';
  if (como === 'direcao') return 'ri-hand-heart-line';
  return 'ri-question-line';
}

function rotuloComo(como: ComoPresenca): string {
  if (como === 'qr') return 'QR';
  if (como === 'foto') return 'Foto';
  if (como === 'direcao') return 'Direção';
  return '—';
}

export default function LinhaPresenca({
  linha,
  tipo,
  salvando,
  liberando,
  aoMarcar,
  aoLiberarReenvio,
  formacaoQuando,
  aoCancelar,
  aoDesfazer,
  desfazendo,
}: Props) {
  const [aberto, setAberto] = useState(false);
  const [obs, setObs] = useState('');

  const cancelada = tipo === 'recusada' && ehCancelada(linha);
  const podeMarcar = tipo !== 'presente' && !cancelada;

  function abrirMarcar() {
    setAberto(true);
    setObs('');
  }

  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center gap-3">
        {tipo === 'presente' && (
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600"
            title={rotuloComo(linha.como)}
          >
            <i className={`${iconeComo(linha.como)} text-base`} />
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="min-w-0 truncate text-sm text-foreground-900">{linha.nome}</span>
            {cancelada && (
              <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-600">
                Cancelada
              </span>
            )}
            {tipo === 'recusada' && !cancelada && (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                recusada
              </span>
            )}
          </div>

          {tipo === 'presente' && !cancelada && (
            <div className="mt-0.5">
              <QuemMarcou linha={linha} formacaoQuando={formacaoQuando} />
            </div>
          )}

          {cancelada && (
            <div className="mt-0.5 space-y-0.5">
              {motivoDoCancelamento(linha) && (
                <p className="text-xs text-foreground-600">{motivoDoCancelamento(linha)}</p>
              )}
              <p className="text-xs text-foreground-600">
                por {linha.avaliada_por_nome || '—'}
                {diaMesHora(quandoMarcou(linha)) ? ` em ${diaMesHora(quandoMarcou(linha))}` : ''}
              </p>
            </div>
          )}

          {tipo === 'recusada' && !cancelada && linha.observacao && (
            <span className="text-xs text-secondary-500">Motivo: {linha.observacao}</span>
          )}
        </div>

        {podeMarcar && !aberto && (
          <button
            type="button"
            onClick={abrirMarcar}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-2 py-1 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Marcar
          </button>
        )}

        {tipo === 'presente' && !cancelada && aoCancelar && (
          <button
            type="button"
            onClick={() => aoCancelar(linha)}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
          >
            Cancelar presença
          </button>
        )}

        {cancelada && aoDesfazer && (
          <button
            type="button"
            onClick={() => aoDesfazer(linha)}
            disabled={desfazendo}
            className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-2 py-1 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
          >
            {desfazendo ? '…' : 'Desfazer'}
          </button>
        )}
      </div>

      {podeMarcar && aberto && (
        <div className="mt-3">
          <input
            type="text"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            placeholder="Observação (ex.: sem celular, chegou atrasado)"
            className="w-full rounded-md border border-secondary-300 bg-white px-3 py-2 text-sm text-foreground-950 outline-none placeholder:text-secondary-400 focus:border-primary-400"
          />
          <div className="mt-2 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => aoMarcar(linha.inscricao_id, obs.trim())}
              disabled={salvando}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {salvando ? '…' : 'Confirmar'}
            </button>
            <button
              type="button"
              onClick={() => setAberto(false)}
              className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {tipo === 'recusada' && !cancelada && linha.tentativas >= 3 && (
        <button
          type="button"
          onClick={() => aoLiberarReenvio(linha.presenca_id as number)}
          disabled={liberando}
          className="mt-2 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-background-100 disabled:opacity-60"
        >
          {liberando ? '…' : 'Liberar mais uma tentativa'}
        </button>
      )}
    </div>
  );
}